import { getPostHogEnvironment, isPostHogCapturing, posthog } from '@/lib/posthog';

const REQUIRED_EVENT_PROPERTIES = ['report_id', 'schema_version'] as const;

const EVENTS_WITHOUT_REPORT_CONTEXT = new Set([
  'user_logged_in',
  'user_logged_out',
]);

type TrackEventProperties = Record<string, unknown>;

type TrackEventOptions = {
  transport?: 'XHR' | 'fetch' | 'sendBeacon';
};

const getPropertyValue = (
  key: string,
  properties?: TrackEventProperties
): unknown => {
  const fromArgs = properties?.[key];
  if (fromArgs !== undefined && fromArgs !== null && fromArgs !== '') {
    return fromArgs;
  }

  if (typeof posthog.get_property === 'function') {
    return posthog.get_property(key);
  }

  return undefined;
};

const hasRequiredProperty = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

export const trackEvent = (
  eventName: string,
  properties?: TrackEventProperties,
  options?: TrackEventOptions
): boolean => {
  if (!isPostHogCapturing()) return false;

  const required = EVENTS_WITHOUT_REPORT_CONTEXT.has(eventName)
    ? []
    : REQUIRED_EVENT_PROPERTIES;

  const missing = required.filter(
    (key) => !hasRequiredProperty(getPropertyValue(key, properties))
  );

  if (missing.length > 0) {
    console.error(
      `[PostHog] Event "${eventName}" blocked. Missing required properties: ${missing.join(', ')}`
    );
    return false;
  }

  const brandId = getPropertyValue('brand_id', properties);
  const brandName = getPropertyValue('brand_name', properties);
  if (hasRequiredProperty(brandId) && !hasRequiredProperty(brandName)) {
    console.error(
      `[PostHog] Event "${eventName}" blocked. Missing required properties: brand_name`
    );
    return false;
  }

  posthog.capture(
    eventName,
    {
      ...properties,
      environment: properties?.environment ?? getPostHogEnvironment(),
    },
    options
  );
  return true;
};
